import { useCallback, useState, useEffect } from "react";
import { Button, Checkbox, Divider, FormControlLabel, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CurrencySymbol from "../../../utils/CurrencySymbol";
import {
  checkedLucidsState,
  isShipmentPriceCalculationState,
  lucidState,
  isEuropeanCountryState,
  isAbove150EURCheckState,
  isEtgbState
} from "../../../atoms/createShipment";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import {
  activeStepState,
  checkedCustomsState,
  checkedInsurancesState,
  ddpState,
  insuranceSlideWarningState,
  insuranceState,
  priceIdState,
  supplierIdState,
  arrivalCountryState,
  productValueState,
  productCurrencyState,
  exchangeRateState
} from "../../../atoms/createShipment";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LocalPrice from "../../../utils/LocalPrice";
import DisplayOverCharges from "./DisplayOverCharges";
import SupplierServicesType from "../../../utils/SupplierServicesType";
import Errors from "../../../utils/Errors";
import iossCountries from "../../../json/iossCountries.json";
import { isSpecialUser, hasPermissionType2, hasPermissionType1Or2 } from "../../../utils/SpecialUserIds";

const ShipmentPriceGetCardItem = ({ item }) => {
  const userInfo = JSON.parse(localStorage.getItem("user"));
  const id = userInfo?.user.id;
  const isSpecial = isSpecialUser(id);
  const hasPermission2 = hasPermissionType2();
  const hasPermission1Or2 = hasPermissionType1Or2();

  const isShipmentPriceCalculation = useRecoilValue(isShipmentPriceCalculationState);
  const arrivalCountry = useRecoilValue(arrivalCountryState);
  const productValue = useRecoilValue(productValueState);
  const productCurrency = useRecoilValue(productCurrencyState);
  const exchangeRate = useRecoilValue(exchangeRateState);
  const isEtgb = useRecoilValue(isEtgbState);

  const [priceId, setPriceId] = useRecoilState(priceIdState);
  const [checkedInsurances, setCheckedInsurances] = useRecoilState(checkedInsurancesState);
  const [checkedCustoms, setCheckedCustoms] = useRecoilState(checkedCustomsState);
  const [checkedLucids, setCheckedLucids] = useRecoilState(checkedLucidsState);
  const [isEuropeanCountry, setIsEuropeanCountry] = useRecoilState(isEuropeanCountryState);
  const [isAbove150EURCheck, setIsAbove150EURCheck] = useRecoilState(isAbove150EURCheckState);

  const setActiveStep = useSetRecoilState(activeStepState);
  const setInsurance = useSetRecoilState(insuranceState);
  const [ddp, setDdp] = useRecoilState(ddpState);

  const setLucid = useSetRecoilState(lucidState);
  const setInsuranceSlideWarning = useSetRecoilState(insuranceSlideWarningState);
  const setSupplierId = useSetRecoilState(supplierIdState);

  const europeanCountriesNames = iossCountries.map((country) => country.country_name_tr);

  const member = JSON.parse(localStorage.getItem("user"));
  const memberId = member?.user?.user_member?.id;

  // DDU checkbox state (supplier 1342, 1350 ve 1343 için)
  const [isDduChecked, setIsDduChecked] = useState(false);

  const [price, setPrice] = useState(() => {
    let initialPrice = parseFloat(item.selling_price);

    const isAmerica = item.country_code_to === 'USA' || item.country_code_to === 'US';

    // Supplier 1342, 1350 veya 1343 için başlangıçta yurtdışı gümrükleme fiyatını ekle
    if (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) {
      if (Number(item.ddp.ddp) !== 0 && Number(item.ddp.ex_custom_price) !== 0) {
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
    }
    // US/USA için permission_type: "2" olanlar için otomatik fiyat hesaplama yap
    else if (isAmerica && hasPermission2) {
      if (Number(item.ddp.ddp) !== 0 && Number(item.ddp.ex_custom_price) !== 0) {
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
    }
    // Özel kullanıcılar için (permission_type: "2" olmayanlar)
    else if (isSpecial) {
      if (item.supplier_id === 339 || checkedCustoms?.map((item) => item.price_id) === item.price_id) {
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
    }

    return initialPrice;
  });


  const calculateIsAbove150EUR = () => {
    // Find the EUR exchange rate
    const euroRate = exchangeRate?.find(rate => rate.currency === "EUR")?.sell_rate;

    let convertedValue = 0;

    // Check if the currency is USD or GBP, then convert it to EUR
    if (productCurrency === "USD") {
      const usdRate = exchangeRate?.find(rate => rate.currency === "USD")?.sell_rate;
      convertedValue = (productValue * usdRate) / euroRate;
    } else if (productCurrency === "GBP") {
      const gbpRate = exchangeRate?.find(rate => rate.currency === "GBP")?.sell_rate;
      convertedValue = (productValue * gbpRate) / euroRate;
    } else if (productCurrency === "EUR") {
      // If already in EUR, just use the productValue directly
      convertedValue = productValue;
    }

    // Return true if the converted value is greater than 150 EUR, false otherwise
    return convertedValue >= 150;

  };

  const handleInsuranceOnChange = useCallback(() => {

    if (checkedInsurances.price_id === item.price_id) {
      setCheckedInsurances({});
      setInsurance(0);
      setPrice(parseFloat(price) - parseFloat(item.insurance.insurance_price));
    } else {
      setInsuranceSlideWarning({ status: true, data: item });
      setCheckedInsurances(item);
      setInsurance(1);
      setPrice(parseFloat(price) + parseFloat(item.insurance.insurance_price));
    }
  }, [
    setInsuranceSlideWarning,
    setCheckedInsurances,
    checkedInsurances,
    setInsurance,
    item,
    setPrice,
    price,
  ]);

  const handleCustomsOnChange = useCallback(() => {
    if (item.supplier_id === 339) {
      return; // Eğer supplier_id 339 ise hiçbir şey yapma
    }

    // Amerika için yurtdışı gümrükleme değişikliğine izin verme
    const isAmerica = item.country_code_to === 'USA' || item.country_code_to === 'US';

    // US/USA için permission_type: "2" olanlar için değiştirmeye izin verme
    // ANCAK: 342, 350, 343 supplier'ları için permission_type 1 veya 2 olanlar manuel seçebilir
    if (isAmerica && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343) {
      return;
    }

    // US/USA için diğer kullanıcılar için değiştirmeye izin verme
    // ANCAK: 342, 350, 343 supplier'ları için tüm kullanıcılar manuel seçebilir
    if (isAmerica && Number(item.ddp.ddp) !== 0 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343) {
      return;
    }

    // Mevcut checkedCustoms'ı al
    const currentChecked = checkedCustoms.map((custom) => custom.price_id);

    if (currentChecked.includes(item.price_id)) {
      // Eğer item zaten checkedCustoms içinde varsa, onu çıkar
      setCheckedCustoms((prev) => prev.filter((custom) => custom.price_id !== item.price_id));
      setDdp(0); // DDP sıfırlanır
      setPrice((prevPrice) => parseFloat(prevPrice) - parseFloat(item.ddp.ex_custom_price)); // Fiyattan gümrükleme ücreti çıkarılır
    } else {
      // Eğer item checkedCustoms içinde yoksa, onu ekle
      setCheckedCustoms((prev) => [...prev, item]); // Yeni item'i ekle
      setDdp(1); // DDP aktif edilir
      setPrice((prevPrice) => parseFloat(prevPrice) + parseFloat(item.ddp.ex_custom_price)); // Fiyata gümrükleme ücreti eklenir
    }
  }, [checkedCustoms, item, setCheckedCustoms, setDdp, setPrice, hasPermission2]);


  const handleLucidOnChange = useCallback(() => {
    if (checkedLucids.price_id === item.price_id) {
      setCheckedLucids({});
      setLucid(0);
      setPrice(parseFloat(price) - parseFloat(item.lucid_price_detail.lucid_price));
    } else {
      setCheckedLucids(item);
      setLucid(1);
      setPrice(parseFloat(price) + parseFloat(item.lucid_price_detail.lucid_price));
    }
  }, [checkedLucids, price, item, setLucid, setCheckedLucids]);

  const choosePriceHandler = useCallback(() => {
    setPriceId(item.price_id);
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setSupplierId(item.supplier_id);
    if (item.price_id === checkedInsurances.price_id) {
      setInsurance(1);
    } else if (item.price_id !== checkedInsurances.price_id) {
      setInsurance(0);
      setCheckedInsurances({});
    }

    const isAmerica = item.country_code_to === 'USA' || item.country_code_to === 'US';

    // Supplier 1342, 1350 veya 1343 için DDU seçiliyse her zaman ddp 0
    if ((item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) && isDduChecked) {
      setDdp(0);
    }
    // Supplier 1342, 1350 veya 1343 için kullanıcının yurtdışı gümrükleme seçimine göre DDP belirle
    else if (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) {
      if (checkedCustoms.some(custom => custom.price_id === item.price_id)) {
        setDdp(1);
      } else {
        setDdp(0);
      }
    }
    // US/USA için permission_type: "2" olanlar için otomatik DDP seçimi (342, 350, 343, 1342, 1350, 1343 hariç)
    else if (isAmerica && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343) {
      // Yurtdışı gümrükleme mevcut ve fiyatı 0 değilse otomatik DDP
      if (Number(item.ddp.ddp) !== 0 && Number(item.ddp.ex_custom_price) !== 0) {
        setDdp(1);
      } else {
        setDdp(0);
      }
    } else {
      // Diğer kullanıcılar için mevcut mantık
      if (checkedCustoms.some(custom => custom.price_id === item.price_id)) {
        setDdp(1);
      } else {
        setDdp(0); // Burayı sadece checkedCustoms içermiyorsa sıfırlayın
        setCheckedCustoms([]);
      }
    }
  }, [
    setCheckedCustoms,
    setCheckedInsurances,
    checkedCustoms,
    checkedInsurances,
    setInsurance,
    setDdp,
    setSupplierId,
    item,
    setActiveStep,
    setPriceId,
    hasPermission2,
    isDduChecked,
  ]);

  const isUSArrival = (() => {
    const val = String(arrivalCountry || "").trim().toLowerCase();
    const synonyms = new Set([
      "us",
      "usa",
      "abd",
      "amerika",
      "amerika birleşik devletleri",
      "united states",
      "united states of america"
    ]);
    const isByName = synonyms.has(val);
    const code = String(item?.country_code_to || "").trim().toUpperCase();
    const isByCode = code === "US" || code === "USA";
    return isByName || isByCode;
  })();

  const displayTransit = (() => {
    const supplierIdNum = Number(item?.supplier_id);
    // Supplier 1342 ve 1350 için transit süreleri (tüm ülkeler için)
    if (supplierIdNum === 1342) return "1-2";
    if (supplierIdNum === 1350) return "2-4";
    // US için özel transit süreleri
    if (!isUSArrival) return item?.transit;
    if (supplierIdNum === 306) return "4-8";
    if (supplierIdNum === 342) return "1-2";
    if (supplierIdNum === 350) return "2-4";
    return item?.transit;
  })();

  const isEmptyValue = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0; // Eğer bir dizi ise ve uzunluğu 0 ise boş kabul ediyoruz.
    } else if (typeof value === "object") {
      return Object.keys(value).length === 0; // Eğer bir obje ise ve anahtar sayısı 0 ise boş kabul ediyoruz.
    }
    return true; // Diğer durumlar için boş kabul ediyoruz.
  };

  const nonEmptyEntries = Object.entries(item?.extra_information).filter(
    ([key, value]) => !isEmptyValue(value)
  );

  const nonEmptyObject = Object.fromEntries(nonEmptyEntries);

  const isEmpty = Number(item.extra_information.total_over_charge_price) === 0;

  useEffect(() => {
    // Fiyatın bir kereye mahsus olarak güncellenmesini sağla
    let initialPrice = parseFloat(item.selling_price);

    const isAmerica = item.country_code_to === 'USA' || item.country_code_to === 'US';
    const isCustomsChecked = checkedCustoms.some(custom => custom.price_id === item.price_id);

    // Supplier 1342, 1350 veya 1343 için başlangıçta yurtdışı gümrükleme seçili (fiyat her zaman gümrükleme dahil)
    if (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) {
      // Yurtdışı gümrükleme mevcut ise her zaman fiyata ekle (DDU seçilse bile fiyat değişmemeli)
      if (Number(item.ddp.ddp) !== 0 && Number(item.ddp.ex_custom_price) !== 0) {
        // Eğer zaten seçili değilse seç (ilk yüklemede)
        if (!isCustomsChecked) {
          setCheckedCustoms((prev) => [...prev, item]); // Yeni item'i ekle
          // DDU seçili değilse ddp = 1, seçiliyse ddp = 0
          setDdp(isDduChecked ? 0 : 1);
          return; // State güncellemesi sonrası çık
        }
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // Permission type 1 veya 2 olanlar için 342, 350, 343 supplier'larında manuel seçim
    // Bu supplier'lar için otomatik seçim yapma
    if ((item.supplier_id === 342 || item.supplier_id === 350 || item.supplier_id === 343) && hasPermission1Or2) {
      // Sadece manuel seçilen gümrükleme için fiyat ekle
      if (isCustomsChecked) {
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // US/USA için permission_type: "2" olanlar için otomatik gümrükleme seçimi yap (342, 350, 343, 1342, 1350, 1343 hariç)
    if (isAmerica && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343) {
      // Yurtdışı gümrükleme mevcut ve fiyatı 0 değilse otomatik seç
      if (Number(item.ddp.ddp) !== 0 && Number(item.ddp.ex_custom_price) !== 0) {
        // Eğer zaten seçili değilse seç (sonsuz döngüyü önlemek için)
        if (!isCustomsChecked) {
          setCheckedCustoms((prev) => [...prev, item]); // Yeni item'i ekle
          setDdp(1); // DDP aktif edilir
          return; // State güncellemesi sonrası çık
        }
        initialPrice += parseFloat(item.ddp.ex_custom_price);
      }
      // Sigorta fiyatını ekleyelim
      if (checkedInsurances.price_id === item.price_id) {
        initialPrice += parseFloat(item.insurance.insurance_price);
      }
      setPrice(initialPrice);
      return;
    }

    // permission_type: "2" olmayan kullanıcılar için Amerika otomatik gümrükleme kontrolü (342, 350, 343, 1342, 1350, 1343 hariç)
    if (isAmerica && Number(item.ddp.ddp) !== 0 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343) {
      if (!isCustomsChecked) {
        setCheckedCustoms((prev) => [...prev, item]); // Yeni item'i ekle
        setDdp(1);
        return; // State güncellemesi sonrası çık
      }
      initialPrice += parseFloat(item.ddp.ex_custom_price);
    }
    // Eğer koşullar sağlanıyorsa customs ekle
    // 302 ve 338 için koşulu ayarlıyoruz
    else if ((item.supplier_id === 338 || item.supplier_id === 302) && isEuropeanCountry && isAbove150EURCheck === true) {
      if (!isCustomsChecked) {
        setCheckedCustoms((prev) => [...prev, item]); // Yeni item'i ekle
        setDdp(1);
        return; // State güncellemesi sonrası çık
      }
      initialPrice += parseFloat(item.ddp.ex_custom_price);
    }
    // Manuel seçilen gümrükleme için fiyat ekle
    else if (isCustomsChecked) {
      initialPrice += parseFloat(item.ddp.ex_custom_price);
    }

    // Price state'ini sadece bir kez ayarla
    setPrice(initialPrice);
  }, [item, setCheckedCustoms, setDdp, isAbove150EURCheck, id, hasPermission2, isDduChecked]);

  useEffect(() => {
    setIsEuropeanCountry(europeanCountriesNames.includes(arrivalCountry));
  }, [setIsEuropeanCountry, arrivalCountry]);

  useEffect(() => {
    const isAbove150EUR = calculateIsAbove150EUR();
    setIsAbove150EURCheck(isAbove150EUR);
  }, [exchangeRate, productCurrency, productValue]);



  return (
    <div className={priceId === item.price_id ? "get-card-wrapper selected" : "get-card-wrapper"}>
      <div className="get-card">
        <div className="get-card-header">
          <div className="get-card-header-headline">
            {item.supplier_note_tr && (
              <Tooltip title={item.supplier_note_tr}>
                <InfoOutlinedIcon sx={{ color: "var(--info)" }} className="logo-warning" />
              </Tooltip>
            )}
            <img
              src={`/images/${item.supplier_logo}`}
              alt={`${item.supplier_name} Logo Görseli`}
              style={{
                marginLeft: item.supplier_note_tr ? 0 : "31px",
                height: "80px", width: "80px"
              }}
            />
            {Number(item?.supplier_id) === 350 ? null : SupplierServicesType(item.service_type)}
            {item.supplier_id === 336 && (
              <span>(ETGB'li)</span>
            )}
            {item.supplier_id === 306 && (
              <span
                style={{
                  display: "inline-block",
                  marginLeft: "8px",
                  padding: "4px 0px",
                  borderTop: "2px solid #d32f2f",
                  borderBottom: "2px solid #d32f2f",
                  color: "#d32f2f",
                  fontWeight: 800,
                  letterSpacing: "1px",
                  textAlign: "center",
                  whiteSpace: "normal",
                  //transform: "rotate(-10deg)",
                  lineHeight: 1.05,
                }}
              >
                TÜM VERGİLER (DDP)
                <br />
                DAHİL
              </span>
            )}
            {item.supplier_id === 307 && (
              <span>(Vergiler Dahil)</span>
            )}
            {item.supplier_id === 308 && (
              <span>(Vergiler Dahil)</span>
            )}
            {item.supplier_id === 309 && (
              <span>(Vergiler Dahil)</span>
            )}
            {item.supplier_id === 701 && (
              <span>(Vergiler Dahil)</span>
            )}
            {item.supplier_id === 801 && (
              <span>(Vergiler Dahil)</span>
            )}
          </div>
          <div className="get-card-header-subtext">
            Teslimat Süresi:&nbsp;<span>{displayTransit}</span>&nbsp;iş günü
          </div>
          {(memberId === 4000000003 || memberId === 4000001083 || memberId === 4000001380 || memberId === "4000001380" || memberId === "4000001084") && (
            <div className="get-card-header-subtext">
              {item.supplier_id}
            </div>
          )}
        </div>
        <Divider />
        <div className="get-card-main">
          <div className="get-card-main-price-wrapper" style={{ position: 'relative' }}>
            <div className="get-card-main-price" style={{
              position: 'relative',
              opacity: (item.supplier_id === 306 && isEtgb === 1) ? 0.6 : 1
            }}>
              <span className="main-price-out">
                {CurrencySymbol(item.currency)}
                {LocalPrice(price)}
              </span>
              <span className="main-price-in">
                &nbsp;({LocalPrice(Number(price) * Number(item.exchange_rate))}
                &nbsp;₺)
              </span>
              {(item.supplier_id === 306 && isEtgb === 1) && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '2px',
                  backgroundColor: '#d32f2f',
                  transform: 'translateY(-50%)',
                  zIndex: 1
                }} />
              )}
            </div>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
              position: 'relative',
              opacity: (item.supplier_id === 306 && isEtgb === 1) ? 0.6 : 1
            }}>
              İade Masrafı: {CurrencySymbol(item.currency)}
              {LocalPrice(item.return_price_detail.return_price)}
              {item.return_price_detail.return_price_txt?.length !== 0 && (
                <Tooltip title={item.return_price_detail.return_price_txt}>
                  <InfoOutlinedIcon
                    sx={{
                      width: "21px!important",
                      height: "21px!important",
                      color: "var(--btnBgBlue)",
                    }}
                  />
                </Tooltip>
              )}
              {(item.supplier_id === 306 && isEtgb === 1) && (
                <div style={{
                  position: 'absolute',
                  top: '50%',
                  left: '0',
                  right: '0',
                  height: '2px',
                  backgroundColor: '#d32f2f',
                  transform: 'translateY(-50%)',
                  zIndex: 1
                }} />
              )}
            </div>
            {(item.supplier_id === 306 && isEtgb === 1) && (
              <div style={{
                marginTop: '8px',
                padding: '8px 12px',
                backgroundColor: '#ffebee',
                border: '1px solid #ffcdd2',
                borderRadius: '4px',
                color: '#d32f2f',
                fontSize: '14px',
                fontWeight: '500'
              }}>
                Bu servis etgb kapsamında değildir. Geri dönüp etgb seçeneğini kaldırabilirsiniz.
              </div>
            )}
          </div>
          {!isShipmentPriceCalculation && (
            <div className="shipmentPrice-get-insurance">
              {Number(item.insurance.insurance_available) === 1 && (
                <FormControlLabel
                  label={`${CurrencySymbol(item.currency)}${LocalPrice(
                    item.insurance.insurance_price
                  )} - Sigorta`}
                  control={
                    <Checkbox
                      checked={checkedInsurances.price_id === item.price_id}
                      onChange={handleInsuranceOnChange}
                      disabled={item.supplier_id === 306 && isEtgb === 1}
                    />
                  }
                />
              )}
              {Number(item?.lucid_price_detail?.lucid) === 1 && (
                <FormControlLabel
                  label={`${CurrencySymbol(item?.lucid_price_detail?.currency)}${LocalPrice(
                    item?.lucid_price_detail?.lucid_price
                  )} - Lucid`}
                  control={
                    <Checkbox
                      checked={checkedLucids.price_id === item.price_id}
                      onChange={handleLucidOnChange}
                      disabled={item.supplier_id === 306 && isEtgb === 1}
                    />
                  }
                />
              )}
              {Number(item.ddp.ddp) !== 0 && item.ddp.ex_custom_price !== 0 && (
                <>
                  <FormControlLabel
                    label={`${CurrencySymbol(item.ddp.currency)}${LocalPrice(
                      item.ddp.ex_custom_price
                    )} - Yurtdışı Gümrükleme`}
                    control={
                      <Checkbox
                        checked={
                          (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) ?
                            // Supplier 1342/1350/1343 için: Her zaman seçili görünsün (DDU checkbox ile ddp kontrol edilir)
                            true :
                            ((item.country_code_to === 'USA' || item.country_code_to === 'US') && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343) ?
                              // US/USA için permission_type: "2" olanlar için her zaman seçili (342, 350, 343 hariç)
                              true :
                              // Diğer kullanıcılar için mevcut mantık
                              checkedCustoms.some(custom => custom.price_id === item.price_id) || ((item.country_code_to === 'USA' || item.country_code_to === 'US') && Number(item.ddp.ddp) !== 0 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343)
                        }
                        onChange={handleCustomsOnChange}
                        disabled={
                          (item.supplier_id === 306 && isEtgb === 1) ||
                            (item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) ? true : // Supplier 1342/1350/1343 için disabled (DDU checkbox ile kontrol edilir)
                            ((item.country_code_to === 'USA' || item.country_code_to === 'US') && hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343) ||
                            item.supplier_id === 339 ||
                            checkedCustoms.some(custom => custom.price_id === item.price_id) && (item.supplier_id === 338 || item.supplier_id === 302) && isEuropeanCountry && isAbove150EURCheck ||
                            ((item.country_code_to === 'USA' || item.country_code_to === 'US') && Number(item.ddp.ddp) !== 0 && !hasPermission2 && item.supplier_id !== 342 && item.supplier_id !== 350 && item.supplier_id !== 343 && item.supplier_id !== 1342 && item.supplier_id !== 1350 && item.supplier_id !== 1343)
                        }
                      />
                    }
                  />
                  {isEuropeanCountry && (
                    <div style={{
                      fontSize: '11px',
                      color: '#666',
                      marginLeft: '0',
                      marginTop: '4px',
                      marginBottom: '8px',
                      fontStyle: 'italic'
                    }}>
                      (Hesaplanan vergi tahminidir. Nihai fatura yurtdışından geldiğinde cari hesabınıza yansıtılacaktır)
                    </div>
                  )}
                </>
              )}
              {(item.supplier_id === 1342 || item.supplier_id === 1350 || item.supplier_id === 1343) && (
                <FormControlLabel
                  label={
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      DDU
                      <Tooltip title="DDU seçilmesi durumunda konşimentonuz DDU hazırlanacaktır. Seçim yapmaz iseniz konşimentonuz DDP hazırlanacaktır">
                        <InfoOutlinedIcon
                          sx={{
                            width: "18px!important",
                            height: "18px!important",
                            color: "var(--btnBgBlue)",
                          }}
                        />
                      </Tooltip>
                    </div>
                  }
                  control={
                    <Checkbox
                      checked={isDduChecked}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIsDduChecked(checked);

                        if (checked) {
                          // DDU seçildiğinde sadece ddp'yi 0 yap (fiyat değişmesin)
                          setDdp(0);
                        } else {
                          // DDU kaldırıldığında sadece ddp'yi 1 yap (fiyat değişmesin)
                          setDdp(1);
                        }
                      }}
                    />
                  }
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--gap)" }}>
        {<Divider sx={{ height: "1px", width: "100%", marginBottom: "var(--gap)" }} />}
        {!isEmpty && (
          <DisplayOverCharges item={item} data={nonEmptyObject} currency={item?.currency} />
        )}
        {!isShipmentPriceCalculation && (
          <Button
            sx={{ ":disabled": { opacity: 0.4 } }}
            className={priceId === item.price_id ? "iyzi-button" : "iyzi-button-blue"}
            onClick={choosePriceHandler}
            disabled={item.supplier_id === 306 && isEtgb === 1}
          >
            {priceId === item.price_id ? (
              <div
                style={{
                  color: "inherit",
                  fontSize: "inherit",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                }}
              >
                <CheckCircleOutlinedIcon /> Seçildi
              </div>
            ) : (
              "Seç"
            )}
          </Button>
        )}
      </div>
      {(item.supplier_id === "337" || item.supplier_id === "338") && (
        <div className="supplier-id-warning">
          <InfoOutlinedIcon className="logo-warning" />
          Bu tedarikçi servisinde iade seçeneği bulunmamaktadır. Adres eksikliği sebebi ile veya
          alıcının adreste bulunmaması durumunda teslim edilemeyen gönderileriniz için sistem
          otomatik olarak imha süreci başlatır.
        </div>
      )}
    </div>
  );
};

export default ShipmentPriceGetCardItem;
